/**
 * Groups denormalized CSV rows (one per Order # + SKU) into order documents
 * with nested items arrays. Runs after the generic willCreate (priority 20)
 * which has already parsed CSV rows into flat objects.
 *
 * Input:  [{ title: 'ORD-001', sku: 'SKU-A' }, { title: 'ORD-001', sku: 'SKU-B' }, ...]
 * Output: [{ title: 'ORD-001', items: [{ sku: 'SKU-A', ... }, { sku: 'SKU-B', ... }], ... }, ...]
 *
 * An order is all or nothing (#2282): when any of its rows is invalid the whole order is
 * rejected rather than built from the rows that happened to pass. Previously the bad row was
 * split off and the order was created without it, so an operator could ship against an order
 * that silently lacked a line from their manifest.
 */
import lodash from 'lodash';
const { omit } = lodash;

/** On the rejected order itself, so no import path creates it as though it were valid. */
export const ORDER_REJECTED_MESSAGE = 'Order rejected: one or more of its rows failed validation';
/** On a row that was fine on its own but belongs to a rejected order. */
export const SIBLING_ROW_REJECTED_MESSAGE = 'Order rejected: another row in this order failed validation';

export default {
    verb: 'willCreate',
    namespace: 'import',
    relation: 'import',
    priority: 19, // runs after generic willCreate (priority 20)
    description: 'Group denormalized CSV rows by Order # into order documents',
    logic: willCreateOrderImport
};

export function willCreateOrderImport({ data }) {
    const { newRecord } = data;
    if (!newRecord || newRecord.foreignNamespace !== 'order' || newRecord.foreignRelation !== 'order') {
        return;
    }

    const { data: parsedRows } = newRecord;
    if (!Array.isArray(parsedRows) || parsedRows.length === 0) return;

    const { orderGroups, orphanRows } = groupRowsByOrder(parsedRows.map(withRowErrors));
    const orders = orderGroups.map(buildOrder);

    // fileSize is left unchanged — it represents the original uploaded file size
    // and is used by the import pipeline for byte-based progress tracking
    // (doingCreate_import_import.js, imported_{namespace}_{relation}.js).
    //
    // sourceRowCount is recorded because from here on the batches carry orders, not rows, and
    // the import summary has to report both (#2282).
    return {
        ...data,
        newRecord: {
            ...newRecord,
            sourceRowCount: parsedRows.length,
            data: [...orders, ...orphanRows]
        }
    };
}

/**
 * Validate one row, collecting every problem on it rather than stopping at the first, so the
 * failed-records CSV tells the operator everything they need to fix in a single pass. Errors the
 * generic parser already found are kept.
 */
function withRowErrors(row) {
    const errors = [...(row.errors || [])];
    // The parser already reports a required column it found empty; saying it twice only
    // clutters the failed-records CSV.
    const alreadyReported = column => errors.some(error => error?.column === column);

    if (!row.title && !alreadyReported('title')) errors.push({ column: 'title', message: 'Order # is required', value: '' });
    if (!row.sku && !alreadyReported('sku')) errors.push({ column: 'sku', message: 'SKU is required', value: '' });

    // A blank cell and an unmapped column both arrive as undefined and mean "not stated",
    // which is one unit. The server normalizes and validates again in
    // willCreate_order_order — this is only so the preview and the `remaining` below agree
    // with what will be stored.
    //
    // A TYPED zero is not one of them. It used to be, because the shared parser turned a
    // blank cell into 0 and the two became indistinguishable here; now that a blank arrives
    // as undefined, a 0 in the file is something the operator actually wrote, and a line
    // expecting zero units would report itself fulfilled before anything was scanned.
    //
    // "Not stated" and "stated wrongly" are deliberately NOT the same thing. Treating both
    // as one unit silently turned a manifest line reading 1.5 into 1, so a customer's bad
    // data became a valid-looking order that nothing could later question. Only the
    // absent cases default; anything else the operator actually typed is reported back.
    if (!isQuantityNotStated(row.expectedQty) && !isWholePositive(row.expectedQty)) {
        errors.push({
            column: 'expectedQty',
            message: 'Expected Qty must be a whole number greater than zero',
            value: row.expectedQty
        });
    }

    return errors.length ? { ...row, errors } : omit(row, ['errors']);
}

/**
 * Collect rows under their Order #, invalid ones included — an invalid row still decides the
 * fate of its order. A row with no Order # belongs to no order and is reported on its own.
 */
function groupRowsByOrder(rows) {
    const groupsByTitle = new Map();
    const orphanRows = [];

    for (const row of rows) {
        if (!row.title) {
            orphanRows.push(row);
            continue;
        }
        if (!groupsByTitle.has(row.title)) groupsByTitle.set(row.title, []);
        groupsByTitle.get(row.title).push(row);
    }

    return { orderGroups: [...groupsByTitle.values()], orphanRows };
}

/**
 * Build one order document from its rows.
 *
 * A rejected order still carries its valid lines and every source row: the server creates it
 * and soft-deletes it straight away, and reports each source row in the failed-records CSV so
 * the operator can fix the file and re-import the whole order.
 */
function buildOrder(orderRows) {
    const validRows = orderRows.filter(row => !row.errors?.length);
    const items = buildLines(validRows);
    const order = {
        title: orderRows[0].title,
        ...orderLevelFieldsOf(validRows[0] ?? orderRows[0]),
        items,
        scanned: 0,
        overScanned: 0,
        // `remaining` counts expected UNITS, so a line of 50 leaves 50 outstanding rather than 1.
        remaining: items.reduce((sum, line) => sum + line.expectedQty, 0),
        progress: 0,
        // Repeated SKUs are merged into one line, so the line count is not the row count — and
        // the import summary reports rows (#2282). The server strips this before writing.
        importRowCount: orderRows.length,
        // Every order carries its rows, accepted ones too: the server can still refuse an order
        // (status, a quantity rule of its own), and once repeated SKUs are merged it could
        // otherwise report only the merged line, not each row of the file. Stripped before writing.
        sourceRows: orderRows
    };

    if (validRows.length === orderRows.length) return order;

    return {
        ...order,
        importRejected: true,
        errors: [ORDER_REJECTED_MESSAGE],
        sourceRows: orderRows.map(row => (row.errors?.length ? row : { ...row, errors: [SIBLING_ROW_REJECTED_MESSAGE] }))
    };
}

/**
 * Sum repeats of the same SKU within one order rather than emitting two lines, so a
 * CSV that lists a SKU twice means "10 of these" rather than two competing lines.
 */
function buildLines(validRows) {
    const items = [];
    for (const row of validRows) {
        const expectedQty = isQuantityNotStated(row.expectedQty) ? 1 : row.expectedQty;
        const existingLine = items.find(line => line.sku === row.sku);
        if (existingLine) {
            existingLine.expectedQty += expectedQty;
        } else {
            items.push({ sku: row.sku, expectedQty });
        }
    }
    return items;
}

/**
 * Separate order-level fields from the per-item ones. expectedQty must be omitted
 * alongside sku: it belongs on the line, and leaving it in would spread it onto the
 * order document as a stray top-level field.
 */
function orderLevelFieldsOf(row) {
    return omit(row, ['title', 'sku', 'expectedQty', 'errors']);
}

function isQuantityNotStated(expectedQty) {
    return expectedQty === undefined || expectedQty === null || expectedQty === '';
}

function isWholePositive(expectedQty) {
    return Number.isInteger(expectedQty) && expectedQty > 0;
}
